import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import { ApplicationMuiTheme } from '@tassat/payments-themes/dist/types/theme';
import { Layout } from 'plotly.js';
import React from 'react';
import Plot from 'react-plotly.js';

import muiStyles from './styles';
import { PieChartComponentProps } from './types';
import { defaultChartColors } from '../chartColors';

export const PieChart: React.FC<PieChartComponentProps> = (props: PieChartComponentProps) => {
    const {
        Caption,
        data,
        noDataCaption,
        chartColors,
        height,
        margin,
        pieSliceTextInfo,
        hovertemplate,
        texttemplate,
        title,
        width
    } = props;

    const theme = useTheme<ApplicationMuiTheme>();
    const classes = muiStyles(props);

    const fontFamily = theme?.typography.fontFamily?.split(',')[0].replaceAll('"', '') || 'Roboto';

    const titleProp: undefined | Layout['title'] = React.useMemo(() => {
        if (!title) return undefined;

        if (typeof title === 'string') {
            return {
                text: title,
                x: 0.025,
                y: 0.975,
                xanchor: 'left',
                font: {
                    color: theme?.app?.clientTheme.colors.primaryColor || '#3C4F70',
                    family: fontFamily,
                    size: 14,
                    weight: 700
                }
            };
        }

        return title;
    }, [ title, theme, fontFamily ]);

    const chartColorArray: Array<string> = React.useMemo(() => {
        const colorArray: Array<string> = [];
        for (let i = 0; i < data.length; i++) {
            if (chartColors?.length) {
                colorArray.push(chartColors[i % chartColors.length]);
            } else {
                colorArray.push(defaultChartColors[i % defaultChartColors.length]);
            }
        }

        return colorArray;
    }, [ data.length, chartColors ]);

    const renderCaption = () => {
        if (typeof Caption === 'string') {
            return (
                <Typography
                    className={classes.caption}
                    data-testid='piechart-caption'
                    variant='body2'
                >
                    {Caption}
                </Typography>
            );
        } else if (Caption) {
            return (<div className={classes.caption}><Caption /></div>);
        }

        return null;
    };

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
        >
            <Plot
                config={{ displaylogo: false }}
                data={[
                    {
                        values: data.map((dataPoint) => dataPoint.value),
                        labels: data.map((dataPoint) => dataPoint.label),
                        text: data.every((dataPoint) => dataPoint.text) ?
                            data.map((dataPoint) => dataPoint.text as string)
                            : undefined,
                        marker: { colors: chartColorArray },
                        direction: 'clockwise',
                        type: 'pie',
                        textinfo: pieSliceTextInfo || 'percent',
                        texttemplate: texttemplate,
                        insidetextfont: {
                            color: '#FFFFFF',
                            family: fontFamily,
                            size: 10,
                            // @ts-ignore
                            weight: 'bold'
                        },
                        insidetextorientation: 'auto',
                        outsidetextfont: {
                            color: 'transparent',
                            family: fontFamily,
                            size: 12
                        },
                        hoverlabel: { font: { color: '#FFFFFF' }},
                        hovertemplate: hovertemplate,
                        domain: { x: [ 0, 0.5 ]},
                        sort: false
                    }
                ]}
                data-testid='plotly-piechart'
                divId='plotly-piechart'
                layout={{
                    annotations: [
                        {
                            text: noDataCaption || 'No data',
                            xref: 'paper',
                            yref: 'paper',
                            showarrow: false,
                            visible: !data.length || data.every((dataPoint) => dataPoint.value === 0),
                            font: {
                                color: theme?.app?.clientTheme.colors.primaryColor || '#375073',
                                family: fontFamily,
                                size: 12
                            }
                        }
                    ],
                    width,
                    height,
                    font: { family: fontFamily },
                    legend: {
                        font: {
                            color: theme?.app?.applicationColors.fontColor || '#202020',
                            size: 12
                        },
                        y: 0.5,
                        yanchor: 'middle',
                        x: 0.75,
                        xanchor: 'left'
                    },
                    margin: {
                        b: margin?.b || 24,
                        l: margin?.l || 32,
                        r: margin?.r || 0,
                        t: margin?.t || 24
                    },
                    title: titleProp
                }}
            />
            {renderCaption()}
        </Grid>
    );
};